import React, { useState, useEffect } from "react";
import styles from "./style.module.scss";
import { apiRequest } from "util/services";
import { notification, Select, Switch, Checkbox } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as actions from "redux/Dashboard/actions.js";
import { UPDATE_USER } from "redux/user/actions.js";
import { useTranslation } from "react-i18next";
import { timezoneList } from "util/constants";
import {
  APP_LANGUAGES,
  applyDocumentLanguage,
  normalizeAppLanguage,
} from "util/locale";
import {
  GlobalOutlined,
  ClockCircleOutlined,
  BgColorsOutlined,
  BellOutlined,
  MailOutlined,
  SlackOutlined,
  CheckOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const normalizeThemeMode = (theme) => {
  if (["dark", "light", "brand"].includes(theme)) return theme;
  return "dark";
};

const SectionCard = ({ icon, title, subtitle, action, children, noPadding }) => (
  <div className={styles.sectionCard}>
    <div className={styles.sectionCardHeader}>
      <div className={styles.sectionCardLeft}>
        <div className={styles.sectionIcon}>{icon}</div>
        <div>
          <p className={styles.sectionTitle}>{title}</p>
          {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
        </div>
      </div>
      {action && <div className={styles.sectionAction}>{action}</div>}
    </div>
    {children && (
      <div className={noPadding ? styles.sectionBodyNoPad : styles.sectionBody}>
        {children}
      </div>
    )}
  </div>
);

// extraBody: always rendered regardless of enabled state (used for integration CTA)
const NotificationCard = ({ icon, title, subtitle, enabled, onToggle, children, extraBody }) => (
  <div className={`${styles.notifCard} ${!enabled && !extraBody ? styles.notifCardDisabled : ""}`}>
    <div className={styles.notifCardHeader}>
      <div className={styles.notifCardLeft}>
        <div className={styles.notifIcon}>{icon}</div>
        <div>
          <p className={styles.notifTitle}>{title}</p>
          <p className={styles.notifSubtitle}>{subtitle}</p>
        </div>
      </div>
      {onToggle && <Switch checked={enabled} onChange={onToggle} />}
    </div>
    {extraBody}
    {enabled && !extraBody && (
      <div className={styles.notifCheckboxRow}>
        {children}
      </div>
    )}
  </div>
);

const Representation = ({
  notificationStatus,
  getSubscribtionStatus,
  subscriptionStatus,
}) => {
  const { i18n, t } = useTranslation();
  const history = useHistory();
  const reduxTheme = useSelector((state) => state.DashboardReducer.theme);
  const [themeMode, setThemeMode] = useState(() =>
    normalizeThemeMode(window.localStorage.getItem("theme") || reduxTheme),
  );
  const [language, setLanguage] = useState(() =>
    normalizeAppLanguage(i18n.language),
  );
  const [status, setStatus] = useState({
    notification_status: true,
    in_platform_status: false,
    in_platform_errors: false,
    in_platform_alerts: false,
    in_platform_updates: false,
    slack_status: false,
    slack_errors: false,
    slack_alerts: false,
    slack_updates: false,
    email_status: false,
    email_updates: false,
    email_errors: false,
    email_alerts: false,
  });
  const [loading, setLoading] = useState(false);
  const [isStatusUpdate, setIsStatusUpdated] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const dispatch = useDispatch();

  const slackConnected = subscriptionStatus?.slack_integration_status === "active";
  const slackHidden = subscriptionStatus?.slack_integration_status === "hidden";

  useEffect(() => {
    setStatus({
      notification_status: true,
      in_platform_status: notificationStatus?.platform?.status === "active",
      in_platform_errors: notificationStatus?.platform?.errors === "active",
      in_platform_alerts: notificationStatus?.platform?.alerts === "active",
      in_platform_updates: notificationStatus?.platform?.updates === "active",
      slack_status: notificationStatus?.slack?.status === "active",
      slack_errors: notificationStatus?.slack?.errors === "active",
      slack_alerts: notificationStatus?.slack?.alerts === "active",
      slack_updates: notificationStatus?.slack?.updates === "active",
      email_status: notificationStatus?.email?.status === "active",
      email_updates: notificationStatus?.email?.updates === "active",
      email_errors: notificationStatus?.email?.errors === "active",
      email_alerts: notificationStatus?.email?.alerts === "active",
    });
    setLoading(false);
    setIsStatusUpdated(false);
  }, [notificationStatus]);

  useEffect(() => {
    if (subscriptionStatus?.timezone) {
      setSelectedTimezone(subscriptionStatus?.timezone);
    } else setSelectedTimezone(Object.keys(timezoneList)[0]);
  }, [subscriptionStatus]);

  const handleUserTheme = async (theme) => {
    const response = await apiRequest("users/theme/setup", "POST", { theme }, true);
    if (response.success) {
      window.localStorage.setItem("theme", theme);
      dispatch({ type: actions.THEME_MODE, payload: { theme } });
    }
    if (response.error) {
      notification.error({ message: response.error });
    }
  };

  const handleUserSelect = (selectedTheme) => {
    setThemeMode(selectedTheme);
    handleUserTheme(selectedTheme);
  };

  const getUserTheme = async () => {
    const response = await apiRequest("users/theme", "POST", true);
    if (response.success) {
      const nextTheme = normalizeThemeMode(response.success);
      setThemeMode(nextTheme);
      window.localStorage.setItem("theme", nextTheme);
      dispatch({ type: actions.THEME_MODE, payload: { theme: nextTheme } });
    } else {
      notification.error({ message: response.error });
    }
  };

  useEffect(() => {
    getUserTheme();
    setLanguage(normalizeAppLanguage(i18n.language));
  }, [i18n.language]);

  const handleLanguageChange = async (value) => {
    const next = normalizeAppLanguage(value);
    const previous = normalizeAppLanguage(i18n.language);

    await i18n.changeLanguage(next);
    window.localStorage.setItem("userLang", next);
    setLanguage(next);
    applyDocumentLanguage(next);

    const res = await apiRequest("users/language/setup", "POST", { lang: next }, true);
    if (res.success) {
      notification.success({ message: i18n.t("others.35") });
    } else {
      await i18n.changeLanguage(previous);
      window.localStorage.setItem("userLang", previous);
      setLanguage(previous);
      applyDocumentLanguage(previous);
      notification.error({ message: res.error });
    }
  };

  const handleTimezoneChange = async (val) => {
    setSelectedTimezone(val);
    const res = await apiRequest("users/timezone/setup", "POST", { timezone: val }, true);
    if (res.success) {
      dispatch({ type: UPDATE_USER, payload: { timezone: val } });
      notification.success({ message: t("settings.254") });
    } else {
      notification.error({ message: res.error });
    }
  };

  const updatePlatformStatus = async () => {
    setLoading(true);
    const response = await apiRequest("notice/notification/preference", "POST", status, true);
    if (response.success) {
      notification.success({ message: t("notification_page.11") });
      getSubscribtionStatus();
    } else {
      notification.error({ message: t("notification_page.12") });
      setLoading(false);
    }
  };

  const handleCheckboxChange = (key, checked) => {
    setStatus((prev) => ({ ...prev, [key]: checked }));
    setIsStatusUpdated(true);
  };

  const handleToggleChange = (key, checked) => {
    setStatus((prev) => ({ ...prev, [key]: checked }));
    setIsStatusUpdated(true);
  };

  const THEMES = [
    { key: "dark", label: t("settings.51"), img: "/assets/settings/theme1.png" },
    { key: "light", label: t("settings.52"), img: "/assets/settings/theme2.png" },
    { key: "brand", label: t("settings.53"), img: "/assets/settings/theme3.png" },
  ];

  // "Hatalar" removed — only Alerts and Updates are user-facing notification types
  const notifTypes = [
    { key: "alerts", label: t("nav.alerts") },
    { key: "updates", label: t("notification_page.17") },
  ];

  const slackCta = !slackConnected ? (
    <div className={styles.integrationCta}>
      <SlackOutlined className={styles.integrationCtaIcon} />
      <div className={styles.integrationCtaText}>
        <span className={styles.integrationCtaTitle}>
          {t("notification_page.slack_cta_title") || "Slack henüz bağlı değil"}
        </span>
        <span className={styles.integrationCtaDesc}>
          {t("notification_page.slack_cta_desc") ||
            "Slack entegrasyonunu kurarak bildirimleri doğrudan kanalınıza alabilirsiniz."}
        </span>
      </div>
      <button
        className={styles.integrationCtaBtn}
        onClick={() => history.push("/settings/integrations")}
        type="button"
      >
        {t("notification_page.slack_cta_btn") || "Bağla"}
        <ArrowRightOutlined style={{ marginLeft: 6, fontSize: 11 }} />
      </button>
    </div>
  ) : null;

  return (
    <div className={styles.wrapperHeader}>
      <h4 className={styles.wrapperTitle}>{t("settings_sidebar.3")}</h4>
      <p className={styles.wrapperSubtitle}>{t("settings.47")}</p>

      {/* ── Language ── */}
      <SectionCard
        icon={<GlobalOutlined />}
        title={t("others.36")}
        subtitle={t("settings.250")}
        action={
          <Select
            style={{ width: 200 }}
            placeholder={t("others.20")}
            onChange={handleLanguageChange}
            value={language}
          >
            {APP_LANGUAGES.map(({ code, nativeLabel }) => (
              <Select.Option value={code} key={code}>
                {nativeLabel}
              </Select.Option>
            ))}
          </Select>
        }
      />

      {/* ── Timezone ── */}
      <SectionCard
        icon={<ClockCircleOutlined />}
        title={t("others.57")}
        subtitle={t("settings.251")}
        action={
          <Select
            style={{ width: 260 }}
            placeholder={t("settings.253")}
            onChange={handleTimezoneChange}
            value={selectedTimezone}
            showSearch
            optionFilterProp="title"
          >
            {Object.keys(timezoneList)?.map((zone) => (
              <Select.Option value={zone} key={zone} title={zone}>
                {zone}
              </Select.Option>
            ))}
          </Select>
        }
      />

      {/* ── Theme ── */}
      <SectionCard
        icon={<BgColorsOutlined />}
        title={t("settings.50")}
        subtitle={t("settings.252")}
        noPadding
      >
        <div className={styles.themesWrapper}>
          {THEMES.map(({ key, label, img }) => {
            const active = themeMode === key;
            return (
              <button
                key={key}
                className={`${styles.themeBox} ${active ? styles.themeBoxActive : ""}`}
                onClick={() => handleUserSelect(key)}
                type="button"
              >
                <div className={styles.themePreview}>
                  <img src={img} alt={label} />
                  {active && (
                    <div className={styles.themeCheckmark}>
                      <CheckOutlined />
                    </div>
                  )}
                </div>
                <span className={styles.themeLabel}>{label}</span>
              </button>
            );
          })}
        </div>
      </SectionCard>

      {/* ── Notifications ── */}
      <div className={styles.notifSection}>
        <p className={styles.notifSectionTitle}>{t("label.12")}</p>
        <p className={styles.notifSectionSubtitle}>
          {t("notification_page.14")}
        </p>

        {/* Platform içi */}
        <NotificationCard
          icon={<BellOutlined />}
          title={t("notification_page.13")}
          subtitle={t("notification_page.14")}
          enabled={status.in_platform_status}
          onToggle={(v) => handleToggleChange("in_platform_status", v)}
        >
          {notifTypes.map(({ key, label }) => (
            <Checkbox
              key={key}
              checked={status[`in_platform_${key}`]}
              onChange={(e) => handleCheckboxChange(`in_platform_${key}`, e.target.checked)}
            >
              {label}
            </Checkbox>
          ))}
        </NotificationCard>

        {/* E-posta */}
        <NotificationCard
          icon={<MailOutlined />}
          title={t("notification_page.18")}
          subtitle={t("notification_page.14")}
          enabled={status.email_status}
          onToggle={(v) => handleToggleChange("email_status", v)}
        >
          {notifTypes.map(({ key, label }) => (
            <Checkbox
              key={key}
              checked={status[`email_${key}`]}
              onChange={(e) => handleCheckboxChange(`email_${key}`, e.target.checked)}
            >
              {label}
            </Checkbox>
          ))}
        </NotificationCard>

        {/* Slack — shown unless plan hides it; CTA when not yet connected */}
        {!slackHidden && (
          <NotificationCard
            icon={<SlackOutlined />}
            title="Slack"
            subtitle={t("notification_page.14")}
            enabled={status.slack_status && slackConnected}
            onToggle={slackConnected ? (v) => handleToggleChange("slack_status", v) : undefined}
            extraBody={slackCta}
          >
            {notifTypes.map(({ key, label }) => (
              <Checkbox
                key={key}
                checked={status[`slack_${key}`]}
                onChange={(e) => handleCheckboxChange(`slack_${key}`, e.target.checked)}
              >
                {label}
              </Checkbox>
            ))}
          </NotificationCard>
        )}

        <div className={styles.saveRow}>
          <button
            className={styles.saveBtn}
            disabled={loading || !isStatusUpdate}
            onClick={updatePlatformStatus}
          >
            {t("button.16")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Representation;
